import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Header from '../components/Header';

const FAQ = ({ cartItems, onRemoveFromCart, onUpdateQuantity }) => {
  const faqData = [
    {
      question: "What is The Favor?",
      answer: "The Favor is a custom party design studio specializing in personalized party decor, favors, paper goods, and digital designs for life's celebrations. From birthdays and weddings to seasonal events, we create cohesive, thoughtfully designed details that bring your vision to life."
    },
    {
      question: "What types of events do you design for?",
      answer: `We design for a wide range of events, including:
• Birthday parties (kids & adults)
• Weddings & engagements
• Baby & bridal showers
• Bachelorette parties
• Graduations
• Holiday & seasonal celebrations

If you have something else in mind, you're always welcome to submit a custom request.`
    },
    {
      question: "Do you offer fully custom designs?",
      answer: "Yes! Custom designs are at the heart of The Favor.\n\nCustom projects begin at $50 and pricing varies based on the number of items, level of customization, and whether you choose digital files or printed products."
    },
    {
      question: "What's the difference between digital and printed items?",
      answer: `Digital Designs
• Delivered via email
• You print at home or through your preferred printer
• Ideal for quick turnarounds and budget-friendly options

Printed Items
• Professionally printed and shipped to you
• Perfect for clients who want a stress-free, polished result
• Printing costs vary based on quantity and materials`
    },
    {
      question: "How long does it take to receive my order?",
      answer: `Digital Orders
• Typically delivered within 3–5 business days
• Rush options may be available

Printed Orders
• Design time: 3–5 business days
• Printing & shipping: varies by item and quantity

We recommend placing printed orders at least 3–4 weeks before your event.`
    },
    {
      question: "Do you offer rush orders?",
      answer: "Yes, rush orders may be available depending on availability and project scope.\nA rush fee will apply. Please indicate your deadline clearly when placing your order or submitting a custom request."
    },
    {
      question: "Can I request revisions?",
      answer: `Yes! Revisions are included based on your package or custom agreement.

Bundles & Pre-Designed Items
• 2 revision rounds included

Custom Design Projects
• 3 revision rounds included

Signature / Wedding / Luxe Projects
• 3–4 revision rounds included (max)
• Only for high-ticket projects. Anything more = additional fee.

Additional revisions beyond what's included may incur a fee.`
    },
    {
      question: "Can you match a specific theme or inspiration?",
      answer: "Absolutely. We love bringing your ideas to life.\n\nYou may share inspiration images, color palettes, or themes. While we don't copy other designers' work, we'll create a design that captures the feel of your inspiration in a unique way."
    },
    {
      question: "Do you use copyrighted characters?",
      answer: "We can design inspired themes, but clients are responsible for confirming usage rights for any copyrighted characters. By placing an order, you acknowledge responsibility for how the designs are used."
    },
    {
      question: "Do you sell individual items or only bundles?",
      answer: "We offer both.\n\nBundles are recommended for a cohesive look and better value, but individual items and custom requests are always available."
    },
    {
      question: "Can I cancel or get a refund?",
      answer: `Due to the custom nature of our designs:
• All sales are final
• No refunds or cancellations once design work has begun

If there's an issue with your order, please contact us — we're always happy to help.`
    },
    {
      question: "Revision Policy",
      answer: `Each order includes a set number of revision rounds based on the selected package.

Bundles & Pre-Designed Items
• 2 revision rounds included

Custom Design Projects
• 3 revision rounds included

Signature / Wedding / Luxe Projects
• 3–4 revision rounds included (max)
• Only for high-ticket projects

Revisions include minor text, color, or layout adjustments. Requests for entirely new concepts, themes, or designs are considered new projects and may require additional fees.

Additional revisions beyond those included may be purchased for an additional fee.`
    },
    {
      question: "How do I get started?",
      answer: `You can:
• Shop our curated bundles
• Browse seasonal collections
• Submit a custom design request

If you're unsure which option is best, feel free to reach out — we'll guide you.`
    },
    {
      question: "Does pricing ever change after purchase?",
      answer: "Bundle pricing includes design and up to 12 printed pieces per included item. Additional quantities are available for an added fee. Custom quotes are provided for large orders."
    },
    {
      question: "What are the standard sizes for printed items?",
      answer: `Most printed items follow these standard sizes:
• Chip bag designs are created in standard 8.5x11 format.
• Candy wrappers are designed to fit standard 1.55 oz Hershey bars.

If you have a specific size requirement, mention it in your custom request and we'll let you know what's possible.`
    },
    {
      question: "Still have questions?",
      answer: "Send us a message or submit a custom request form.\nWe'd love to design something special for your celebration."
    }
  ];

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header
        cartItems={cartItems}
        onRemoveFromCart={onRemoveFromCart}
        onUpdateQuantity={onUpdateQuantity}
      />

      {/* FAQ Hero */}
      <section className="pt-40 md:pt-44 pb-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-charcoal-ink mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-stone-taupe">
            Everything you need to know about The Favor
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-lg border border-stone-taupe/20 px-6"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-charcoal-ink hover:text-stone-taupe transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-charcoal-ink/80 whitespace-pre-line leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-soft-linen">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-ink mb-4">
            Ready to start designing your celebration?
          </h2>
          <p className="text-xl text-stone-taupe mb-8">
            Explore our bundles or submit a custom request today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button
                size="lg"
                className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory text-lg px-8 py-6"
              >
                Shop Bundles
              </Button>
            </Link>
            <Link to="/?action=custom">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-charcoal-ink text-charcoal-ink hover:bg-soft-linen text-lg px-8 py-6"
              >
                Submit Custom Request
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal-ink text-warm-ivory py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_partyfavor-design/artifacts/kg186rqs_Photoroom_20260106_152029.png" 
                alt="The Favor" 
                className="h-20 md:h-24 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-warm-ivory/80">
                Custom party details, designed to be remembered.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Shop</h4>
              <ul className="space-y-2">
                <li><Link to="/#bundles" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Party Bundles</Link></li>
                <li><Link to="/#occasions" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Occasions</Link></li>
                <li><Link to="/?action=custom" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Custom Requests</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Information</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">About</Link></li>
                <li><Link to="/faq" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">FAQ</Link></li>
                <li><Link to="/turnaround-time" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Turnaround Times</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.instagram.com/thefavor.design/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-warm-ivory/20 pt-8 text-center text-warm-ivory/60">
            <p>&copy; 2025 The Favor. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
